import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Badge, Spinner, Row, Col, Button, Offcanvas } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaChevronRight, FaInfoCircle, FaBookOpen, FaAward, FaUserCheck, FaLayerGroup, FaDownload, FaSyncAlt, FaFilter } from "react-icons/fa";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Components
import Header from "../components/Header";
import Footer from "../components/Footer";
import API_BASE_URL from "../config";
import logo from "../assets/logo.png";

const AdmissionProcedure = () => {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("undergraduate");
  const [lastSync, setLastSync] = useState(new Date().toLocaleTimeString());
  const [showDrawer, setShowDrawer] = useState(false); // Mobile Drawer State

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fetch/admissionData`);
      if (response.data?.[0]) {
        setRawData(response.data[0]);
        setLastSync(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error("Unable to sync admission procedures.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // --- PDF EXPORT LOGIC ---
  const downloadPDF = () => {
    if (!rawData || !currentSection) return;
    const doc = new jsPDF();
    
    // 1. University Branding (Letterhead)
    try {
      doc.addImage(logo, 'PNG', 14, 10, 25, 25);
    } catch (e) {
      console.warn("Logo not found for PDF", e);
    }
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(0, 43, 90);
    doc.text("SUKKUR IBA UNIVERSITY", 45, 20);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Merit - Quality - Excellence", 45, 26);
    doc.text("Official Admission Guidelines", 45, 31);
    
    doc.setDrawColor(0, 85, 164);
    doc.setLineWidth(0.5);
    doc.line(14, 40, 196, 40);

    // 2. Title Section
    doc.setFontSize(16);
    doc.setTextColor(0, 43, 90);
    doc.text(`${currentSection.title} Guidelines`, 14, 52);
    
    let startY = 60;

    // 3. Content Handling
    if (activeTab === "seatAllocation" && currentSection.data?.departments) {
      currentSection.data.departments.forEach((dept) => {
        if (startY > 250) { doc.addPage(); startY = 20; }
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text(dept.name, 14, startY);
        startY += 7;

        autoTable(doc, {
          startY: startY,
          head: [["Program", "Capacity"]],
          body: dept.programs.map(p => [p.name, p.seats]),
          theme: 'striped',
          headStyles: { fillColor: [0, 85, 164] },
          margin: { left: 14, right: 14 },
        });
        startY = doc.lastAutoTable.finalY + 15;
      });
    } else if (currentSection.data?.sections || currentSection.data?.details) {
      const contentData = currentSection.data.sections || currentSection.data.details;
      contentData.forEach((section) => {
        if (startY > 250) { doc.addPage(); startY = 20; }
        
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 85, 164);
        doc.text(section.subtitle || section.label || "Requirement", 14, startY);
        startY += 10;

        const details = section.details || section.points || [];
        details.forEach((item) => {
          if (startY > 260) { doc.addPage(); startY = 20; }
          
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(80);

          if (typeof item === 'string') {
            const splitText = doc.splitTextToSize(`- ${item}`, 170);
            doc.text(splitText, 18, startY);
            startY += (splitText.length * 5) + 5;
          } else {
            doc.setFont("helvetica", "bold");
            doc.setTextColor(0, 43, 90);
            doc.text(`> ${item.label}`, 18, startY);
            startY += 7;
            doc.setFont("helvetica", "normal");
            doc.setTextColor(80);

            if (item.text) {
              const splitText = doc.splitTextToSize(item.text, 165);
              doc.text(splitText, 22, startY);
              startY += (splitText.length * 5) + 5;
            }
            if (item.points) {
               item.points.forEach(p => {
                 if (startY > 270) { doc.addPage(); startY = 20; }
                 doc.text(`* ${p}`, 25, startY);
                 startY += 5;
               });
               startY += 4;
            }
          }
        });
        startY += 5;
      });
    }

    // 4. Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Generated on ${new Date().toLocaleDateString()} | SIBA Admissions Portal`, 14, 285);
      doc.text(`Page ${i} of ${pageCount}`, 196, 285, { align: "right" });
    }

    doc.save(`${currentSection.title}_Official_Guidelines.pdf`);
  };

  const sections = useMemo(() => {
    if (!rawData) return [];
    return [
      { id: "undergraduate", title: rawData.undergraduate?.title, icon: <FaBookOpen />, data: rawData.undergraduate },
      { id: "admissionCriteria", title: rawData.admissionCriteria?.title, icon: <FaAward />, data: rawData.admissionCriteria },
      { id: "graduate", title: rawData.graduate?.title, icon: <FaUserCheck />, data: rawData.graduate },
      { id: "seatAllocation", title: "Seat Allocation", icon: <FaLayerGroup />, data: rawData.seatAllocation }
    ];
  }, [rawData]);

  const filteredSections = sections.filter(s => 
    s.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const currentSection = sections.find(s => s.id === activeTab);

  if (loading) return (
    <div className="premium-loader">
      <Spinner animation="border" variant="primary" />
      <p className="mt-3 fw-bold letter-spacing-2">LOADING PROCEDURES</p>
    </div>
  );

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* --- SUB-NAV BAR --- */}
      <section className="portal-sub-nav py-4 shadow-sm bg-white sticky-top no-print" style={{ top: '0', zIndex: 1020 }}>
        <Container>
          <Row className="align-items-center">
            <Col md={5}>
              <h1 className="h4 fw-900 text-navy m-0">Admission <span className="text-primary">Procedures</span></h1>
              <small className="text-muted d-flex align-items-center gap-2 mt-1">
                 <span className="sync-dot-mini"></span> Sync: {lastSync}
              </small>
            </Col>
            <Col md={7}>
              <div className="d-flex gap-3 justify-content-end align-items-center mt-3 mt-md-0">
                <div className="search-pill-container flex-grow-1">
                  <FaSearch className="search-icon" />
                  <input type="text" placeholder="Search guidelines..." onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                {/* Mobile Filter Toggle */}
                <Button 
                    variant="primary" 
                    className="rounded-pill px-4 d-lg-none d-flex align-items-center gap-2 nav-hint-pulse shadow-sm h-100"
                    onClick={() => setShowDrawer(true)}
                >
                    <FaFilter /> <span className="fw-bold small">Select Module</span>
                </Button>
                <div className="d-none d-md-flex gap-2">
                    <Button variant="outline-primary" className="rounded-pill fw-bold h-100" onClick={fetchData}>
                        <FaSyncAlt />
                    </Button>
                    {currentSection && (
                      <Button variant="primary" className="rounded-pill px-4 fw-bold h-100" onClick={downloadPDF}>
                          <FaDownload className="me-2" /> PDF
                      </Button>
                    )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MAIN VIEWPORT --- */}
      <section className="portal-viewport bg-light-soft py-5">
        <Container className="main-portal-container section-standard-spacing position-relative" style={{ zIndex: 10 }}>
          <Row className="g-4">
            {/* Desktop Sidebar */}
            <Col lg={4} className="d-none d-lg-block no-print">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm">
                  <div className="card-label">PROCEDURE MODULES</div>
                  <div className="program-list custom-scrollbar">
                    {filteredSections.map((sec) => (
                      <button 
                        key={sec.id} 
                        className={`prog-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeTab === sec.id ? 'is-active' : ''}`} 
                        onClick={() => setActiveTab(sec.id)}
                      >
                         <div className="d-flex align-items-center gap-3">
                          <div className={`icon-circle-mini ${activeTab === sec.id ? 'active' : ''}`}>
                            {sec.icon}
                          </div>
                          <div className="text-start">
                              <div className="name fw-bold mb-0">{sec.title}</div>
                              <div className="stats-tag">Requirement</div>
                          </div>
                        </div>
                        <FaChevronRight className={`chevron-mini ${activeTab === sec.id ? 'visible' : ''}`} />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 p-4 rounded-4 bg-primary text-white shadow-lg">
                  <h5 className="fw-bold">Need Help?</h5>
                  <p className="small opacity-75">If you're unsure about the process, contact our admission helpdesk.</p>
                  <Button variant="light" size="sm" className="rounded-pill px-4 fw-bold">Contact Support</Button>
                </div>
              </div>
            </Col>

            {/* Content Display */}
            <Col lg={8} className="print-full-width">
              <AnimatePresence mode="wait">
                {currentSection ? (
                  <motion.div 
                    key={activeTab} 
                    initial={{ opacity: 0, x: 10 }} 
                    animate={{ opacity: 1, x: 0 }} 
                    exit={{ opacity: 0, x: -10 }}
                    className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm h-100"
                  >
                    <div className="section-intro mb-5">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="category-tag">Official Guidelines</div>
                        <Button variant="link" className="text-primary d-lg-none no-print" onClick={downloadPDF}><FaDownload /> PDF</Button>
                      </div>
                      <h2 className="portal-heading text-navy display-5 mb-3">{currentSection.title}</h2>
                      <div className="ivy-divider mb-4" />
                      <p className="editorial-text-body text-muted mb-0">Official admission rules and step-by-step procedures for the current academic session.</p>
                    </div>

                    {/* Content Mapping (Kept exactly as original) */}
                    {currentSection.data.sections && currentSection.data.sections.map((section, sIdx) => (
                      <div key={sIdx} className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm mb-4 border-top border-primary border-4 page-break">
                        <h3 className="h5 fw-800 text-primary mb-4 text-uppercase letter-spacing-1">
                           {section.subtitle}
                        </h3>
                        
                        {section.details.map((detail, dIdx) => (
                          <div key={dIdx} className="mb-4">
                            <h6 className="fw-700 text-navy mb-2 d-flex align-items-center">
                              <div className="dot-indicator me-2" style={{background: '#0055a4'}} />
                              {detail.label}
                            </h6>
                            {detail.text && <p className="text-muted ms-3 border-start ps-3">{detail.text}</p>}
                            
                            {detail.points && (
                              <ul className="list-unstyled ms-4 mt-2">
                                {detail.points.map((p, pIdx) => (
                                  <li key={pIdx} className="small text-muted mb-2">
                                    <FaChevronRight className="text-primary me-2 no-print" style={{fontSize: '0.7rem'}} /> {p}
                                  </li>
                                ))}
                              </ul>
                            )}

                            {detail.details && (
                              <Row className="g-3 mt-2">
                                {detail.details.map((sub, subIdx) => (
                                  <Col md={6} key={subIdx}>
                                    <div className="p-3 rounded-3 bg-light border h-100 shadow-inner">
                                      <div className="fw-bold text-primary mb-2 small">{sub.title}</div>
                                      {sub.items.map((item, iIdx) => (
                                        <div key={iIdx} className="small py-1 border-bottom-dashed d-flex justify-content-between">
                                          <span>{item}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}

                    {activeTab === "admissionCriteria" && currentSection.data.details?.[0]?.points && (
                      <div className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm mb-4 border-top border-warning border-4 page-break">
                         {currentSection.data.details[0].points.map((point, pIdx) => (
                           <div key={pIdx} className="d-flex mb-3 p-3 bg-light-soft rounded-3 border-start border-warning border-3">
                              <span className="fw-bold text-primary me-3">{pIdx + 1}.</span>
                              <span className="small text-navy">{point}</span>
                           </div>
                         ))}
                      </div>
                    )}

                    {activeTab === "seatAllocation" && currentSection.data.departments && (
                      <div className="phase-block bg-white p-4 p-lg-5 rounded-4 shadow-sm page-break">
                        {currentSection.data.departments.map((dept, dIdx) => (
                          <div key={dIdx} className="mb-5">
                            <h5 className="fw-bold text-navy border-bottom pb-2 mb-3">{dept.name}</h5>
                            <div className="table-responsive">
                              <table className="table table-hover align-middle">
                                <thead className="bg-light">
                                  <tr>
                                    <th className="small fw-bold">PROGRAM NAME</th>
                                    <th className="small fw-bold text-center">SEATS</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dept.programs.map((prog, pIdx) => (
                                    <tr key={pIdx}>
                                      <td className="small fw-bold text-muted">{prog.name}</td>
                                      <td className="text-center"><Badge bg="secondary" className="rounded-pill px-3">{prog.seats}</Badge></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {dept.note && <div className="alert alert-info py-2 small shadow-sm">{dept.note}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  </motion.div>
                ) : (
                  <div className="text-center py-5">
                    <FaInfoCircle size={40} className="text-muted mb-3" />
                    <p>No procedures found matching your search.</p>
                  </div>
                )}
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- MOBILE PROCEDURE DRAWER --- */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="h-50 rounded-top-4">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold text-navy">Select Procedure</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <div className="program-list-mobile">
            {filteredSections.map((sec) => (
                <button 
                key={sec.id} 
                className={`prog-item mobile-item w-100 d-flex align-items-center justify-content-between p-3 border-0 bg-transparent transition-all ${activeTab === sec.id ? 'is-active' : ''}`} 
                onClick={() => { setActiveTab(sec.id); setShowDrawer(false); }}
                >
                <div className="d-flex align-items-center gap-3">
                    <div className={`icon-circle-mini ${activeTab === sec.id ? 'active' : ''}`}>
                         {sec.icon}
                    </div>
                    <div className="text-start">
                        <div className="name fw-bold mb-0">{sec.title}</div>
                        <div className="stats-tag">Requirement</div>
                    </div>
                </div>
                {activeTab === sec.id && <FaChevronRight className="chevron-mini visible" />}
                </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
      
      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #fff; min-height: 100vh; font-family: 'Inter', sans-serif; }
        .bg-light-soft { background-color: #f8faff; }
        .fw-900 { font-weight: 900; }
        .text-navy { color: #002b5a; }
        .ls-1 { letter-spacing: 1px; }

        /* Typography Sync */
        .category-tag { color: #0055a4; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 2px; }
        .portal-heading { font-weight: 900; letter-spacing: -1.5px; line-height: 1.1; }
        .ivy-divider { width: 60px; height: 5px; background: #0055a4; border-radius: 10px; }
        .editorial-text-body { font-size: 1.1rem; line-height: 1.6; color: #475569; }

        .sync-dot-mini {
          width: 8px; height: 8px; background: #10b981;
          border-radius: 50%; display: inline-block;
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-green {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }

        .main-portal-container { margin-top: 0; padding-bottom: 100px; position: relative; z-index: 10; }
        
        .search-pill-container {
          background: #f1f4f9; border-radius: 50px; padding: 10px 25px;
          display: flex; align-items: center; transition: 0.3s;
        }
        .search-pill-container:focus-within { background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.05); border: 1px solid #0055a4; }
        .search-pill-container input { border: none; background: transparent; outline: none; width: 100%; margin-left: 12px; font-weight: 500; color: #002b5a; }
        .search-icon { color: #94a3b8; }

        /* Sidebar Styling */
        .sticky-sidebar { position: sticky; top: 120px; z-index: 100; transition: top 0.3s; }
        .sidebar-card { background: #fff; border-radius: 24px; overflow: hidden; display: flex; flex-direction: column; max-height: 75vh; border: 1px solid #e2e8f0 !important; }
        
        .card-label { 
          padding: 18px 25px; background: #002b5a; color: white; 
          font-weight: 800; font-size: 0.75rem; letter-spacing: 2px; text-transform: uppercase;
        }

        .program-list { overflow-y: auto; flex: 1; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

        .prog-item {
          transition: 0.3s;
          border-bottom: 1px solid #f1f5f9 !important;
          cursor: pointer;
        }
        .prog-item:hover { background-color: #f8fafc !important; }
        .prog-item.is-active {
          background-color: #002b5a !important;
          border-left: 6px solid #ffc300 !important;
        }
        .prog-item.is-active .name { color: white !important; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }

        .icon-circle-mini {
          width: 42px; height: 42px; background: #f8fafc; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem; color: #64748b; transition: 0.3s;
          border: 1px solid #e2e8f0;
        }

        .stats-tag { font-size: 0.75rem; color: #94a3b8; font-weight: 600; }
        .chevron-mini { font-size: 0.8rem; color: #cbd5e0; opacity: 0; transition: 0.3s; transform: translateX(-10px); }
        .chevron-mini.visible { opacity: 1; transform: translateX(0); color: #0055a4; }
        .prog-item.is-active .chevron-mini.visible { color: #ffc300; }

        .details-card { border-radius: 24px !important; border: 1px solid #e2e8f0 !important; }

        .phase-block { border-top: 4px solid #0055a4 !important; border-radius: 20px !important; }
        .dot-indicator { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
        
        .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }
        .border-bottom-dashed { border-bottom: 1px dashed #e2e8f0; }

        @media (max-width: 991px) {
          .main-portal-container { margin-top: 0px; }
          .section-standard-spacing { padding-bottom: 30px; }
          .portal-heading { font-size: 1.6rem; word-break: break-word; letter-spacing: -1px; }
          .category-tag { font-size: 0.7rem; letter-spacing: 1.5px; }
          .details-card { padding: 1rem !important; }
          .phase-block { padding: 1.25rem !important; }
          .editorial-text-body { font-size: 0.95rem; }
          
          .program-list-mobile .prog-item.mobile-item { border-bottom: 1px solid #f1f5f9 !important; padding: 15px; }
          .program-list-mobile .prog-item.mobile-item.is-active { 
            background: #002b5a !important; 
            color: white !important; 
            border-left: 5px solid #ffc300 !important; 
          }
          .program-list-mobile .prog-item.mobile-item.is-active .name { color: white !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .stats-tag { color: rgba(255,255,255,0.7) !important; }
          .program-list-mobile .prog-item.mobile-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); border-color: rgba(255,255,255,0.2); color: white; }
          .sticky-sidebar { position: relative !important; top: 0 !important; }
        }

        /* Navigation Hint Pulse */
        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(0, 85, 164, 0); }
          100% { box-shadow: 0 0 0 0 rgba(0, 85, 164, 0); }
        }
        .nav-hint-pulse {
          animation: hintPulse 2s infinite;
          border: 2px solid #fff !important;
        }

        /* PRINT STYLES */
        @media print {
          .no-print, header, footer, .portal-sub-nav { display: none !important; }
          body { background: white !important; }
          .details-card { box-shadow: none !important; border: none !important; padding: 0 !important; }
          .phase-block { box-shadow: none !important; border: 1px solid #eee !important; page-break-inside: avoid; margin-bottom: 2rem !important; }
          .page-break { page-break-before: always; }
          .portal-viewport { padding-top: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default AdmissionProcedure;